// Finishing a canvas op offers the matching move for the boat's pieces (docs/canvas-plan.md session 4):
// "Remove canvas" → Mark pulled, "Canvas — send out for cleaning" → Send out, "Reinstall canvas" →
// Put back on boat. Which move and which pieces: canvasCore.opMove. Used by the office's Complete
// button (app.js submitOpen) and the techs app's Finish operation (English or Spanish).
// Nothing to move → nothing shown. The op is already finished; skipping only leaves the pieces as they were.

import { escapeHTML as esc } from './utilities';
import { opMove, pieceName, actionLabel, outText, errorText } from './canvasCore';
import { loadBoatPieces, loadSpots, loadLofts, runBatch } from './canvasData';

const T = {
  pull: { EN: 'The canvas came off: mark these pulled?', ES: 'Se quitó la lona: ¿marcar estas piezas como quitadas?' },
  sendOut: { EN: 'Send these to a loft?', ES: '¿Enviar estas piezas a la velería?' },
  install: { EN: 'Put these back on the boat?', ES: '¿Poner estas piezas en el barco?' },
  stillOut: { EN: 'Still out at a loft, so not back on the boat:', ES: 'Todavía en la velería:' },
  storedAt: { EN: 'Stored at', ES: 'Guardada en' },
  spotPh: { EN: 'e.g. Canvas loft bin 12, In the boat', ES: 'p. ej. Altillo, caja 12' },
  loft: { EN: 'Loft', ES: 'Velería' },
  choose: { EN: 'Choose…', ES: 'Elegir…' },
  dueBack: { EN: 'Due back (optional)', ES: 'Regresa (opcional)' },
  notNow: { EN: 'Not now', ES: 'Ahora no' },
  ok: { EN: 'OK', ES: 'OK' },
  saving: { EN: 'Saving…', ES: 'Guardando…' },
  pickOne: { EN: 'Tick at least one piece.', ES: 'Marca al menos una pieza.' },
  failed: { EN: 'Couldn\'t save.', ES: 'No se pudo guardar.' },
  done: { EN: '{n} updated', ES: '{n} actualizadas' },
  title: { EN: 'Canvas & sails', ES: 'Lonas y velas' },
};

/**
 * op: the finished operation ({ Boat_ID, Customer_ID, Short_Description, ... }).
 * opts: { lang: 'EN' | 'ES', toast(text), onDone() }. Resolves when the box closes (or at once when
 * there's nothing to offer or the pieces couldn't be read, e.g. offline).
 */
export async function offerCanvasMove(op, opts = {}) {
  if (!op?.Boat_ID) return;
  let pieces;
  try { pieces = await loadBoatPieces(op.Boat_ID); } catch (err) { console.warn('Canvas offer: pieces not available', err); return; }
  const plan = opMove(op, pieces);
  if (!plan) return;
  const L = opts.lang === 'ES' ? 'ES' : 'EN';
  const t = key => T[key][L];
  const { action } = plan;
  const [spots, lofts] = await Promise.all([
    action === 'pull' ? loadSpots().catch(() => []) : [],
    action === 'sendOut' ? loadLofts().catch(() => []) : [],
  ]);
  const vendorOf = plan.pieces.find(p => p.Vendor_ID)?.Vendor_ID || '';

  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.className = 'cv-offer-overlay';
    overlay.innerHTML = `
      <div class="cv-offer" role="dialog" aria-modal="true" aria-labelledby="cv-offer-title">
        <div class="cv-offer-kicker">${esc(t('title'))} · ${esc(op.Boat_Name || '')}</div>
        ${plan.pieces.length ? `
          <div class="cv-offer-title" id="cv-offer-title">${esc(t(action))}</div>
          <div class="cv-offer-list">${plan.pieces.map(p => `
            <label class="cv-offer-row"><input type="checkbox" class="cv-check" value="${esc(p.id)}" checked> ${esc(pieceName(p, L))}</label>`).join('')}
          </div>
          ${action === 'install' ? '' : `<div class="cv-form-grid">
            ${action === 'pull' ? `
              <label class="cv-field"><span>${esc(t('storedAt'))}</span>
                <input type="text" name="location" list="cv-offer-spots" maxlength="120" value="${esc(plan.location)}" placeholder="${esc(t('spotPh'))}" autocomplete="off">
              </label>
              <datalist id="cv-offer-spots">${spots.map(s => `<option value="${esc(s)}"></option>`).join('')}</datalist>` : ''}
            ${action === 'sendOut' ? `
              <label class="cv-field"><span>${esc(t('loft'))}</span>
                <select name="vendorId"><option value="">${esc(t('choose'))}</option>${lofts.map(v => `<option value="${esc(v.id)}"${v.id === vendorOf ? ' selected' : ''}>${esc(v.name)}</option>`).join('')}</select>
              </label>
              <label class="cv-field cv-field-narrow"><span>${esc(t('dueBack'))}</span><input type="date" name="dueBack"></label>` : ''}
          </div>`}` : ''}
        ${plan.away.length ? `<div class="cv-offer-warn"${plan.pieces.length ? '' : ' id="cv-offer-title"'}><b>${esc(t('stillOut'))}</b><br>${esc(outText(plan.away)).replace(/\n/g, '<br>')}</div>` : ''}
        <div class="cv-error" hidden></div>
        <div class="cv-form-buttons">
          ${plan.pieces.length ? `<button type="button" class="cv-btn cv-btn-primary" data-offer="go">${esc(actionLabel(action, L))}</button>` : ''}
          <button type="button" class="cv-btn" data-offer="skip">${esc(plan.pieces.length ? t('notNow') : t('ok'))}</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const box = overlay.querySelector('.cv-offer');
    const errBox = overlay.querySelector('.cv-error');
    const close = () => { overlay.remove(); document.removeEventListener('keydown', onKey); resolve(); };
    const onKey = e => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
    overlay.querySelector('[data-offer="skip"]').onclick = close;
    (overlay.querySelector('input[name="location"]') || overlay.querySelector('[data-offer]')).focus();

    const go = overlay.querySelector('[data-offer="go"]');
    if (go) go.onclick = async () => {
      const ids = new Set([...box.querySelectorAll('.cv-offer-list input:checked')].map(i => i.value));
      const chosen = plan.pieces.filter(p => ids.has(p.id));
      const show = msg => { errBox.textContent = msg; errBox.hidden = !msg; };
      if (!chosen.length) { show(t('pickOne')); return; }
      const vendorSel = box.querySelector('select[name="vendorId"]');
      const input = {
        location: box.querySelector('input[name="location"]')?.value || '',
        vendorId: vendorSel?.value || '',
        vendorName: vendorSel?.value ? vendorSel.selectedOptions[0].textContent : '',
        dueBack: box.querySelector('input[name="dueBack"]')?.value || '',
        wo: op.Work_Order ? String(op.Work_Order) : '',
      };
      go.disabled = true;
      go.textContent = t('saving');
      try {
        const { moved, refused } = await runBatch(chosen, action, input);
        if (refused.length && !moved.length) throw refused[0].err;
        opts.toast?.(t('done').replace('{n}', moved.length));
        opts.onDone?.();
        close();
      } catch (err) {
        if (!err?.canvasCode) console.error('Canvas offer: save failed', err);
        show(errorText(err, L) || t('failed'));
        go.disabled = false;
        go.textContent = actionLabel(action, L);
      }
    };
  });
}
